import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";

type RoleKey = "admin" | "manager" | "operator";

interface RequireRoleProps {
  roles: RoleKey[];
  children: React.ReactNode;
}

/**
 * Единая точка контроля доступа к staff-роутам.
 * Ничего не рендерит, пока роль не подтверждена, и редиректит без прав.
 */
export const RequireRole = ({ roles, children }: RequireRoleProps) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isManager, isOperator, loading: roleLoading, roleError, retry } = useUserRole();
  const location = useLocation();

  const allowed =
    (roles.includes("admin") && isAdmin) ||
    (roles.includes("manager") && isManager) ||
    (roles.includes("operator") && isOperator);

  const denied = !authLoading && !roleLoading && !!user && !roleError && !allowed;

  useEffect(() => {
    if (denied) {
      toast.error("Доступ запрещён. Недостаточно прав.");
    }
  }, [denied]);

  if (authLoading || roleLoading) {
    return (
      <div className="container py-20 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Роль не удалось проверить (медленный интернет) — не выкидываем, даём повторить
  if (roleError) {
    return (
      <div className="container py-20 flex flex-col items-center gap-4 text-center">
        <p className="text-lg font-medium">Не удалось проверить права доступа</p>
        <p className="text-muted-foreground max-w-md">
          Похоже, интернет сейчас работает медленно или с перебоями. Проверьте подключение и попробуйте ещё раз.
        </p>
        <button
          type="button"
          onClick={retry}
          className="px-5 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
